import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { profileKeys } from '@entities/Profile';
import { cn } from '@shared/lib';
import { resendLabelKey, useResendCooldown } from '../model/useResendCooldown';
import { EmailConfirmNote } from './EmailConfirmNote';
import { EmailText } from './EmailText';

interface EmailVerifyBannerProps {
  email: string;
  /** full — блок «Подтверди почту» там, где без подтверждения не сделать то, ради чего пришёл
   *  (свои карточки, подборки), тот же, что на итоге знакомства; compact — в настройках;
   *  dock — плашка вверху главной (решение владельца 2026-10-21; до этого стояла на месте строки создания,
   *  ТЗ главной § 10): одно сообщение и одна кнопка, строка создания под ней видна, но неактивна. */
  variant?: 'full' | 'compact' | 'dock';
  /** Только для full: без заголовка, когда его уже говорит сама страница. */
  title?: boolean;
  /** dock: почта уже подтверждена (ответ «уже подтверждена» или перечитанный профиль). */
  onVerified?: () => void;
  /** dock: нажали «Отправить ещё раз» — для события аналитики страницы. */
  onResend?: () => void;
  /** dock на телефоне: две строки текста (адрес переносится после «@»), кнопка 44 px — справа под ними. */
  stacked?: boolean;
  className?: string;
}

export function EmailVerifyBanner({
  email,
  variant = 'full',
  title = true,
  onVerified,
  onResend,
  stacked = false,
  className,
}: EmailVerifyBannerProps) {
  if (variant === 'dock') {
    return <DockBanner email={email} onVerified={onVerified} onResend={onResend} stacked={stacked} className={className} />;
  }
  if (variant === 'compact') return <CompactBanner email={email} />;
  // Прежний крупный баннер (градиент, заголовок-засечка, моноширинный адрес) на телефоне разваливался
  // и повторял заголовок страницы (замечание владельца 2026-10-19).
  return <EmailConfirmNote email={email} showTitle={title} className="mb-6" />;
}

/** Не чаще раза в 10 с: вкладку переключают часто, а профиль — тяжёлый запрос AppGate. */
const PROFILE_RECHECK_MS = 10_000;

/**
 * На месте строки создания на главной: почту подтверждают в другой вкладке или в браузере вместо
 * приложения, а `refetchOnWindowFocus` в приложении выключен — поэтому при возврате на вкладку
 * профиль перечитывается сам (AppGate обновит `emailVerified`, и строка откроется без перезагрузки).
 */
function DockBanner({
  email,
  onVerified,
  onResend,
  stacked,
  className,
}: {
  email: string;
  onVerified?: () => void;
  onResend?: () => void;
  stacked: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const lastCheck = useRef(0);
  const verified = useRef(onVerified);
  verified.current = onVerified;
  const { resend, state, cooldown, disabled } = useResendCooldown({
    onAlreadyVerified: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.current });
      verified.current?.();
    },
  });

  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheck.current < PROFILE_RECHECK_MS) return;
      lastCheck.current = now;
      void qc.invalidateQueries({ queryKey: profileKeys.current });
    };
    document.addEventListener('visibilitychange', recheck);
    return () => document.removeEventListener('visibilitychange', recheck);
  }, [qc]);

  const button = (
    <button
      type="button"
      onClick={() => {
        onResend?.();
        void resend();
      }}
      disabled={disabled}
      className="ml-auto min-h-11 flex-none whitespace-nowrap rounded-pill px-3 text-[14px] font-medium text-warn transition-colors hover:bg-elev disabled:cursor-not-allowed disabled:text-muted disabled:hover:bg-transparent"
    >
      {t(`emailVerify.full.${resendLabelKey[state]}`, { n: cooldown })}
    </button>
  );
  // Адрес — ПДн: amp-mask / ym-hide-content прячут его в записях сессий.
  const address = (
    <span className="amp-mask ym-hide-content text-fg [overflow-wrap:anywhere]">
      <EmailText email={email} />
    </span>
  );

  if (stacked) {
    // Телефон: просьба и адрес — двумя строками во всю ширину (рядом с кнопкой текст сжимался бы в
    // колонку — замечание владельца 2026-10-19), кнопка 44 px — справа под ними.
    return (
      <div className={cn('rounded-[18px] border border-solid border-line bg-card pb-1 pl-4 pr-2 pt-2.5 text-[14px]', className)}>
        <div className="flex items-start gap-3 pr-2">
          <span aria-hidden className="flex-none pt-0.5 text-[16px] leading-5 text-warn">
            ✉
          </span>
          <p className="min-w-0 flex-1 leading-5">
            <span className="block font-medium text-fg">{t('emailVerify.nudge.title')}</span>
            <span className="text-muted">{t('emailVerify.nudge.bodyPrefix')} </span>
            {address}
          </p>
        </div>
        <div className="flex">{button}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex min-h-16 flex-wrap items-center gap-x-3 gap-y-1 rounded-[20px] border border-solid border-line bg-card py-2.5 pl-4 pr-2 text-[14px]',
        className,
      )}
    >
      <span aria-hidden className="flex-none text-[16px] leading-none text-warn">
        ✉
      </span>
      <p className="min-w-0 flex-1 leading-[1.4]">
        <span className="font-medium text-fg">{t('emailVerify.nudge.title')}</span>
        <span className="text-muted"> · {t('emailVerify.nudge.bodyPrefix')} </span>
        {address}
      </p>
      {button}
    </div>
  );
}

function CompactBanner({ email }: { email: string }) {
  const { t } = useTranslation();
  const { resend, state, cooldown, disabled } = useResendCooldown();
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-warn/30 bg-warn/10 px-[18px] py-3.5 nav:flex-row nav:items-center nav:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-warn/20 text-base text-warn">
          ✉
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 text-[14px] font-medium text-fg">
            {t('emailVerify.compact.titlePrefix')}{' '}
            <span className="amp-mask ym-hide-content font-mono text-xs text-warn [overflow-wrap:anywhere]">
              <EmailText email={email} />
            </span>{' '}
            {t('emailVerify.compact.titleSuffix')}
          </div>
          <div className="text-xs leading-[1.5] text-muted">{t('emailVerify.compact.body')}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={disabled}
        className={`w-full whitespace-nowrap rounded-pill px-4 py-2.5 text-xs font-semibold tracking-[0.02em] transition-colors nav:w-auto ${
          disabled ? 'cursor-not-allowed bg-elev text-muted' : 'bg-warn text-fg-inv hover:brightness-105'
        }`}
      >
        {t(`emailVerify.compact.${resendLabelKey[state]}`, { n: cooldown })}
      </button>
    </div>
  );
}
